import { useState } from 'react';
import { applyReferrer } from './api';
import ShareKit from './ShareKit';

const COUNTRIES = [
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'OTHER', label: 'Other' },
];
const REFERRALS_PER_MONTH = ['0-10', '11-20', '21-50', '50-100', '100+'];

const EMPTY = {
  fullName: '', email: '', phone: '', country: '',
  // bank (payout) — fields shown depend on country
  bankName: '', holderName: '', accountNumber: '', sortCode: '', routingNumber: '', iban: '', swift: '',
  expectedReferralsPerMonth: '', referredBy: '',
  understandsRules: false, agreeTerms: false, understandPayoutsAfterSuccess: false,
};

const digits = (s) => (s || '').replace(/\D/g, '');

// Referrer community WhatsApp group. Surfaced on the post-signup success screen
// so a brand-new referrer can join the group for tips, updates and support.
const WHATSAPP_GROUP_URL = 'https://chat.whatsapp.com/Lvaf5x3TbCz1IlF39C9iNx?mode=gi_t';

// Attribution: a known `?src=` on the URL prefills the "who referred you" field
// so operators can see where an applicant came from (e.g. the takeover wizard's
// completion page links here with ?src=takeover-complete). Unknown/absent src
// leaves the field empty. Editable — a real referrer can overwrite it.
const SRC_LABELS = { 'takeover-complete': 'Takeover completion page' };

function srcPrefill() {
  try {
    const src = new URLSearchParams(window.location.search).get('src');
    return (src && SRC_LABELS[src]) || '';
  } catch {
    return '';
  }
}

export default function SignupPage() {
  const [form, setForm] = useState(() => ({ ...EMPTY, referredBy: srcPrefill() }));
  const [errors, setErrors] = useState({});
  const [submitErr, setSubmitErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { referralUrl, portalUrl } on success
  const [copied, setCopied] = useState('');

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const isUK = form.country === 'GB';
  const isUS = form.country === 'US';
  const isOther = form.country === 'OTHER';

  const validate = () => {
    const e = {};
    if (!form.fullName.trim() || !/\s/.test(form.fullName.trim())) e.fullName = 'Enter your full name';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (form.phone.trim().length < 3) e.phone = 'Enter your phone number';
    if (!form.country) e.country = 'Select your country';
    if (form.bankName.trim().length < 2) e.bankName = "Enter your bank's name";
    if (form.holderName.trim().length < 2) e.holderName = 'Enter the name on the account';
    if (isUK) {
      if (digits(form.sortCode).length !== 6) e.sortCode = 'Sort code must be 6 digits (e.g. 12-34-56)';
      if (digits(form.accountNumber).length !== 8) e.accountNumber = 'UK account number must be 8 digits';
    } else if (isUS) {
      if (digits(form.routingNumber).length !== 9) e.routingNumber = 'Routing number must be 9 digits';
      if (digits(form.accountNumber).length < 4) e.accountNumber = 'Account number must be at least 4 digits';
    } else if (isOther) {
      if (form.iban.replace(/\s+/g, '').length < 15) e.iban = 'Enter a valid IBAN';
    }
    if (!form.expectedReferralsPerMonth) e.expectedReferralsPerMonth = 'Please choose an option';
    if (!form.understandsRules) e.understandsRules = 'Required';
    if (!form.agreeTerms) e.agreeTerms = 'Required';
    if (!form.understandPayoutsAfterSuccess) e.understandPayoutsAfterSuccess = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    setSubmitErr('');
    if (!validate()) return;
    setLoading(true);
    try {
      const bankDetails = {
        bankName: form.bankName.trim(),
        holderName: form.holderName.trim(),
      };
      if (isUK) {
        bankDetails.sortCode = digits(form.sortCode);
        bankDetails.accountNumber = digits(form.accountNumber);
      } else if (isUS) {
        bankDetails.routingNumber = digits(form.routingNumber);
        bankDetails.accountNumber = digits(form.accountNumber);
      } else {
        bankDetails.iban = form.iban.trim().replace(/\s+/g, '').toUpperCase();
        if (form.swift.trim()) bankDetails.swift = form.swift.trim().toUpperCase();
      }
      const res = await applyReferrer({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        country: form.country,
        bankDetails,
        expectedReferralsPerMonth: form.expectedReferralsPerMonth,
        referredBy: form.referredBy.trim() || null,
        understandsRules: form.understandsRules,
        agreeTerms: form.agreeTerms,
        understandPayoutsAfterSuccess: form.understandPayoutsAfterSuccess,
      });
      setResult(res || {});
    } catch (err) {
      // Surface the server's actual reason (e.g. duplicate email) rather than
      // assuming every 409 is an email clash — the message comes from the API.
      if (err.status === 409) setSubmitErr(err.message || 'An application with this email already exists.');
      else setSubmitErr(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, which) => {
    navigator.clipboard?.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(''), 1500);
  };

  if (result) {
    return (
      <div className="wrap">
        <div className="brand">VANTAGE · Referral Program</div>
        <h1 className="title">You're all set 🎉</h1>
        <div className="banner-ok">
          Thanks, {form.fullName.split(' ')[0]}! Your referrer account is active. Here's your unique referral link —
          share it, and you earn £10 for every successful referral. Save both links below now — copy your referral
          link and bookmark your portal; this is the only time we'll show them.
        </div>
        {result.referralUrl && (
          <div className="card">
            <h2>Your referral link</h2>
            <div className="link-box">
              <code>{result.referralUrl}</code>
              <button className="btn-ghost" onClick={() => copy(result.referralUrl, 'ref')}>{copied === 'ref' ? 'Copied!' : 'Copy'}</button>
            </div>
          </div>
        )}
        <ShareKit referralUrl={result.referralUrl} />
        {result.portalUrl && (
          <div className="card">
            <h2>Your portal</h2>
            <p className="lead" style={{ marginTop: 0 }}>Track your clicks, referrals and earnings here:</p>
            <div className="link-box">
              <code>{result.portalUrl}</code>
              <button className="btn-ghost" onClick={() => copy(result.portalUrl, 'portal')}>{copied === 'portal' ? 'Copied!' : 'Copy'}</button>
            </div>
          </div>
        )}
        <div className="card">
          <h2>Join the community &amp; earn more</h2>
          <p className="lead" style={{ marginTop: 0 }}>
            Our referrers' WhatsApp group is where you earn the most — join now so you don't miss out:
          </p>
          <ul className="community-perks">
            <li>🏆 <b>Regular competitions &amp; prize draws</b> — members are entered to win cash and prizes</li>
            <li>💰 Insider tips and support to land <b>more successful referrals</b> — more £10 payouts</li>
            <li>🔔 Be first to hear about <b>bonuses</b>, updates and new ways to earn</li>
          </ul>
          <a
            className="btn-whatsapp"
            href={WHATSAPP_GROUP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Join our WhatsApp group
          </a>
        </div>
      </div>
    );
  }

  const field = (k, label, opts = {}) => (
    <div className="form-field">
      <label>{label}{opts.required && <span className="req"> *</span>}</label>
      {opts.select ? (
        <select value={form[k]} onChange={(e) => set(k, e.target.value)}>
          <option value="">{opts.placeholder || 'Select…'}</option>
          {opts.options.map((o) => {
            const val = typeof o === 'string' ? o : o.value;
            const lab = typeof o === 'string' ? o : o.label;
            return <option key={val} value={val}>{lab}</option>;
          })}
        </select>
      ) : (
        <input type={opts.type || 'text'} inputMode={opts.inputMode} value={form[k]}
          onChange={(e) => set(k, e.target.value)} placeholder={opts.placeholder || ''} />
      )}
      {errors[k] && <div className="form-err">{errors[k]}</div>}
    </div>
  );

  return (
    <div className="wrap">
      {/* Existing referrers land on this signup page too — keep "Log in" prominent
          at the very top so they don't have to scroll past the whole form to reach
          their portal. */}
      <div className="topbar">
        <div className="brand">VANTAGE · Referral Program</div>
        <a className="btn-login" href="#/login">Log in to your portal</a>
      </div>
      <h1 className="title">Earn £10 for every successful referral</h1>
      <p className="lead">Refer people with an existing LinkedIn account and get paid when they're onboarded.</p>

      <div className="scheme">
        <ul>
          <li><b>£10</b> to you for every successful referral</li>
          <li><b>£20</b> to the account holder you refer</li>
          <li>Existing LinkedIn accounts only — must be <b>over 6 months old</b></li>
          <li><b>One account per person</b> — multiple accounts from the same person don't qualify</li>
          <li>Payments are made <b>within 24 hours</b> of successful completion</li>
        </ul>
      </div>

      {submitErr && <div className="banner-err">{submitErr}</div>}

      <div className="card">
        <h2>Personal details</h2>
        <div className="grid">
          {field('fullName', 'Full name', { required: true })}
          {field('email', 'Email', { required: true, type: 'email' })}
          {field('phone', 'Phone number', { required: true, type: 'tel' })}
          {field('country', 'Country', { required: true, select: true, options: COUNTRIES, placeholder: 'Select your country' })}
        </div>
      </div>

      <div className="card">
        <h2>Bank details</h2>
        {!form.country && <p className="lead" style={{ marginTop: 0 }}>Select your country above to enter your bank details.</p>}
        {form.country && (
          <>
            <div className="grid">
              {field('bankName', 'Bank name', { required: true })}
              {field('holderName', 'Name on account', { required: true })}
            </div>
            {isUK && (
              <div className="grid">
                {field('sortCode', 'Sort code', { required: true, inputMode: 'numeric', placeholder: '12-34-56' })}
                {field('accountNumber', 'Account number', { required: true, inputMode: 'numeric', placeholder: '8 digits' })}
              </div>
            )}
            {isUS && (
              <div className="grid">
                {field('routingNumber', 'Routing number', { required: true, inputMode: 'numeric', placeholder: '9 digits' })}
                {field('accountNumber', 'Account number', { required: true, inputMode: 'numeric' })}
              </div>
            )}
            {isOther && (
              <div className="grid">
                {field('iban', 'IBAN', { required: true, placeholder: 'e.g. GB29 NWBK 6016 1331 9268 19' })}
                {field('swift', 'SWIFT / BIC (optional)')}
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <h2>Referral scheme</h2>
        {field('expectedReferralsPerMonth', 'How many referrals do you expect to send per month? 👇', { required: true, select: true, options: REFERRALS_PER_MONTH })}
        {field('referredBy', 'Who referred you to this page? (optional)')}
        <label className="check">
          <input type="checkbox" checked={form.understandsRules} onChange={(e) => set('understandsRules', e.target.checked)} />
          <span>I understand the rules: existing LinkedIn accounts only, over 6 months old, one account per person.{errors.understandsRules && <span className="form-err"> {errors.understandsRules}</span>}</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.agreeTerms} onChange={(e) => set('agreeTerms', e.target.checked)} />
          <span>I agree to the referral scheme terms.{errors.agreeTerms && <span className="form-err"> {errors.agreeTerms}</span>}</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.understandPayoutsAfterSuccess} onChange={(e) => set('understandPayoutsAfterSuccess', e.target.checked)} />
          <span>I understand payouts only happen after successful referrals.{errors.understandPayoutsAfterSuccess && <span className="form-err"> {errors.understandPayoutsAfterSuccess}</span>}</span>
        </label>
      </div>

      <button className="primary-cta" onClick={submit} disabled={loading}>
        {loading ? 'Submitting…' : 'Become a referrer'}
      </button>

      <p className="lead" style={{ marginTop: 18, textAlign: 'center' }}>
        Already a referrer? <a href="#/login">Log in</a>
      </p>
    </div>
  );
}
